import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { ScrollToTop } from "./components/layout/ScrollToTop";
import Index from "./pages/Index";
import { CookieConsent } from "./components/CookieConsent";
import { locations } from "./data/locations";

// Eager: homepage + the primary conversion routes. Everything else is code-split
// to keep the initial JS bundle small (matters for Core Web Vitals — INP/TBT).
const Services = lazy(() => import("./pages/Services"));
const Packages = lazy(() => import("./pages/Packages"));
const About = lazy(() => import("./pages/About"));
const BookACall = lazy(() => import("./pages/BookACall"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const InternationalServices = lazy(() => import("./pages/InternationalServices"));
const PayrollServices = lazy(() => import("./pages/PayrollServices"));
const TaxReturnsSarsCompliance = lazy(() => import("./pages/TaxReturnsSarsCompliance"));
const AnnualFinancialStatements = lazy(() => import("./pages/AnnualFinancialStatements"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogCategory = lazy(() => import("./pages/BlogCategory"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Author = lazy(() => import("./pages/Author"));
const BookkeepingHub = lazy(() => import("./pages/pillars/BookkeepingHub"));
const VATHub = lazy(() => import("./pages/pillars/VATHub"));
const CIPCHub = lazy(() => import("./pages/pillars/CIPCHub"));
const Tools = lazy(() => import("./pages/Tools"));
const VATCalculator = lazy(() => import("./pages/tools/VATCalculator"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const CookieStatus = lazy(() => import("./pages/CookieStatus"));
const SeoStatus = lazy(() => import("./pages/SeoStatus"));
const Auth = lazy(() => import("./pages/Auth"));
const MonthlyBookkeepingServices = lazy(() => import("./pages/services/MonthlyBookkeepingServices"));
const PayrollEmp201Emp501 = lazy(() => import("./pages/services/PayrollEmp201Emp501"));
const UifMonthlyDeclarations = lazy(() => import("./pages/services/UifMonthlyDeclarations"));
const AnnualTaxReturns = lazy(() => import("./pages/services/AnnualTaxReturns"));
const ProvisionalTaxReturns = lazy(() => import("./pages/services/ProvisionalTaxReturns"));
const CipcAnnualReturns = lazy(() => import("./pages/services/CipcAnnualReturns"));
const BeneficialOwnershipSubmission = lazy(() => import("./pages/services/BeneficialOwnershipSubmission"));
const WorkmensCompensation = lazy(() => import("./pages/services/WorkmensCompensation"));
const AreasIndex = lazy(() => import("./pages/areas/AreasIndex"));
const CityHubPage = lazy(() => import("./pages/areas/CityHubPage"));
const SuburbPage = lazy(() => import("./pages/areas/SuburbPage"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true" />
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
          >
            Skip to main content
          </a>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/packages" element={<Packages />} />
              <Route path="/about" element={<About />} />
              <Route path="/book-a-call" element={<BookACall />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* Legacy flat /bookkeeping-services-{city} URLs — permanently
                  consolidated to the /areas hub. React Router's <Navigate replace>
                  produces a client-side redirect; publishing hosting should
                  configure a matching 301 rewrite for full SEO effect. */}
              {locations.map((loc) => (
                <Route
                  key={loc.slug}
                  path={`/bookkeeping-services-${loc.slug}`}
                  element={<Navigate to={loc.canonicalTo ?? `/areas/${loc.slug}`} replace />}
                />
              ))}
              <Route path="/international-services" element={<InternationalServices />} />
              <Route path="/payroll-services" element={<PayrollServices />} />
              <Route path="/tax-returns-sars-compliance" element={<TaxReturnsSarsCompliance />} />
              <Route path="/annual-financial-statements" element={<AnnualFinancialStatements />} />
              <Route path="/monthly-bookkeeping-services" element={<MonthlyBookkeepingServices />} />
              <Route path="/payroll-emp201-emp501" element={<PayrollEmp201Emp501 />} />
              <Route path="/uif-monthly-declarations" element={<UifMonthlyDeclarations />} />
              <Route path="/annual-tax-returns" element={<AnnualTaxReturns />} />
              <Route path="/provisional-tax-returns" element={<ProvisionalTaxReturns />} />
              <Route path="/cipc-annual-returns" element={<CipcAnnualReturns />} />
              <Route path="/beneficial-ownership-submission" element={<BeneficialOwnershipSubmission />} />
              <Route path="/workmens-compensation" element={<WorkmensCompensation />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/category/:category" element={<BlogCategory />} />
              <Route path="/blog/category/:category/page/:page" element={<BlogCategory />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/author/bookkeeping-services-south-africa" element={<Author />} />
              <Route path="/bookkeeping" element={<BookkeepingHub />} />
              <Route path="/vat" element={<VATHub />} />
              <Route path="/cipc" element={<CIPCHub />} />
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/vat-calculator" element={<VATCalculator />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/cookie-status" element={<CookieStatus />} />
              <Route path="/seo-status" element={<SeoStatus />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/areas" element={<AreasIndex />} />
              <Route path="/areas/:city" element={<CityHubPage />} />
              <Route path="/areas/:city/:suburb" element={<SuburbPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
